import {collectComponentVariantIds, EMPTY_CONFIGURATION, parseConfiguration, parseDraftConfiguration} from '../configurator.js';
import {optionCounts} from '../option-counts.js';
import {checkConfigurations} from '../configuration-health.js';
import {migrateHandleSettings} from '../metaobject-handles.js';
import {convertPrereleaseConfiguration, configurationStructureErrors, authoredConfiguration} from '../schema-v1.js';
import {fetchDisclaimers} from './disclaimers';

const NAMESPACE = 'evp_configurator';
const CONFIGURED_KEY = 'configured';
const CONFIGURATION_KEY = 'settings';
const COMPONENTS_KEY = 'component_variants';
const DRAFT_CONFIGURATION_KEY = 'settings_draft';
const DRAFT_COMPONENTS_KEY = 'component_variants_draft';

let adminEndpoint = 'shopify:admin/api/2026-07/graphql.json';
export function useLocalAdminEndpoint() { adminEndpoint = '/__dev/graphql'; }

export function gqlFetch(query: string, variables?: Record<string, unknown>) {
  return fetch(adminEndpoint, {
    method: 'POST',
    headers: {'Content-Type': 'application/json'},
    body: JSON.stringify({query, variables}),
  }).then(async (response) => {
    const json = await response.json();
    if (!response.ok || json.errors?.length) throw new Error(json.errors?.[0]?.message || 'Shopify request failed.');
    return json;
  });
}

export function throwUserErrors(payload: any) {
  if (payload?.userErrors?.some((error: any) => error.code === 'INVALID_COMPARE_DIGEST' || error.code === 'STALE_OBJECT')) throw new Error('Configuration changed in another tab. Reload before saving or publishing; your changes have not been written.');
  if (payload?.userErrors?.length) throw new Error(payload.userErrors.map((error: any) => error.message).join('\n'));
}

export async function ensureConfiguratorDefinitions() {
  const json = await gqlFetch(`#graphql
    query EVPConfiguratorDefinitions {
      metafieldDefinitions(first: 20, ownerType: PRODUCT, query: "namespace:${NAMESPACE}") {
        nodes { id namespace key type { name } access { storefront } capabilities { adminFilterable { enabled } } }
      }
    }
  `);
  const definitions = json.data.metafieldDefinitions.nodes;
  for (const definition of [
    {key: CONFIGURED_KEY, name: 'Configurator configured', description: 'Maintained by EVP Configurator. True when a draft or published configuration exists.', type: 'boolean'},
    {key: CONFIGURATION_KEY, name: 'Configurator settings', description: 'Product-level native option, custom-option, condition, and Includes configuration.', type: 'json'},
    {key: COMPONENTS_KEY, name: 'Configurator variants', description: 'Live Shopify variant references used by the product configurator.', type: 'list.variant_reference'},
    {key: DRAFT_CONFIGURATION_KEY, name: 'Configurator settings (draft)', description: 'Unpublished configuration used by the editor and public draft preview.', type: 'json'},
    {key: DRAFT_COMPONENTS_KEY, name: 'Configurator variants (draft)', description: 'Variant references for the unpublished configuration preview.', type: 'list.variant_reference'},
  ]) {
    const existing = definitions.find((item: any) => item.namespace === NAMESPACE && item.key === definition.key);
    const marker = definition.key === CONFIGURED_KEY;
    if (existing) {
      if (marker) {
        if (existing.type.name !== 'boolean' || !existing.capabilities?.adminFilterable?.enabled) throw new Error('Configurator configured must be a filterable boolean definition.');
      } else if (existing.type.name !== definition.type || existing.access.storefront !== 'PUBLIC_READ') {
        throw new Error(`${NAMESPACE}.${definition.key} exists but does not use the required ${definition.type} type with storefront public-read access.`);
      }
      continue;
    }
    const created = await gqlFetch(`#graphql
      mutation CreateEVPConfiguratorDefinition($definition: MetafieldDefinitionInput!) {
        metafieldDefinitionCreate(definition: $definition) {
          createdDefinition { id namespace key }
          userErrors { field message code }
        }
      }
    `, {definition: {...definition, namespace: NAMESPACE, ownerType: 'PRODUCT', ...(marker ? {pin: false, capabilities: {adminFilterable: {enabled: true}}} : {access: {storefront: 'PUBLIC_READ'}})}});
    throwUserErrors(created.data.metafieldDefinitionCreate);
  }
}

export async function countConfiguredProducts() {
  const json = await gqlFetch(`#graphql
    query ConfiguredProductsCount {
      productsCount(query: "metafields.${NAMESPACE}.${CONFIGURED_KEY}:true", limit: null) { count }
    }
  `);
  return json.data.productsCount.count;
}

export async function listConfiguredProducts() {
  const products = [];
  let after: string | null = null;
  let hasNextPage = true;
  while (hasNextPage) {
    const json = await gqlFetch(`#graphql
      query ConfiguredProducts($after: String) {
        products(first: 250, after: $after, sortKey: TITLE, query: "metafields.${NAMESPACE}.${CONFIGURED_KEY}:true") {
          nodes {
            id title status templateSuffix featuredMedia { preview { image { url } } }
            configuration: metafield(namespace: "${NAMESPACE}", key: "${CONFIGURATION_KEY}") { jsonValue }
            draft: metafield(namespace: "${NAMESPACE}", key: "${DRAFT_CONFIGURATION_KEY}") { jsonValue }
          }
          pageInfo { hasNextPage endCursor }
        }
      }
    `, {after});
    products.push(...json.data.products.nodes.filter((product: any) => product.configuration?.jsonValue != null || product.draft?.jsonValue != null));
    hasNextPage = json.data.products.pageInfo.hasNextPage;
    after = json.data.products.pageInfo.endCursor;
  }
  let health: any[] = [];
  try { health = await checkConfigurations(products.flatMap((p: any) => [p.configuration?.jsonValue, p.draft?.jsonValue]), gqlFetch); }
  catch { health = products.flatMap(() => [{status: 'error', issues: []}, {status: 'error', issues: []}]); }
  return products.map((product: any, index: number) => ({
    health: product.configuration ? health[index * 2] : null,
    draftHealth: product.draft ? health[index * 2 + 1] : null,
    id: product.id,
    title: product.title,
    templateSuffix: product.templateSuffix ?? '',
    status: product.status,
    imageUrl: product.featuredMedia?.preview?.image?.url || '',
    summary: {...configurationSummary(product.draft?.jsonValue ?? product.configuration?.jsonValue), ...optionCounts(product.draft?.jsonValue ?? product.configuration?.jsonValue)},
    publicationStatus: product.configuration ? (product.draft && JSON.stringify(product.draft.jsonValue) !== JSON.stringify(product.configuration.jsonValue) ? 'Unpublished changes' : 'Published') : 'Draft only',
  }));
}

export async function fetchProductConfiguration(productId: string) {
  const json = await gqlFetch(`#graphql
    query ConfigurableProduct($id: ID!) {
      shop { myshopifyDomain }
      product(id: $id) {
        id title handle status
        options { id name position optionValues { id name hasVariants } }
        variants(first: 250) { nodes { id title sku price compareAtPrice } }
        configuration: metafield(namespace: "${NAMESPACE}", key: "${CONFIGURATION_KEY}") { jsonValue compareDigest }
        draft: metafield(namespace: "${NAMESPACE}", key: "${DRAFT_CONFIGURATION_KEY}") { jsonValue compareDigest }
        notices: metafield(namespace: "${NAMESPACE}", key: "disclaimers") { compareDigest }
        draftNotices: metafield(namespace: "${NAMESPACE}", key: "disclaimers_draft") { compareDigest }
        legacyGroup: metafield(namespace: "${NAMESPACE}", key: "product_group") { value reference { ... on Metaobject { id handle } } }
        legacySwitcherDraft: metafield(namespace: "${NAMESPACE}", key: "product_group_draft") { jsonValue }
        components: metafield(namespace: "${NAMESPACE}", key: "${COMPONENTS_KEY}") {
          compareDigest jsonValue
          references(first: 128) { nodes { ... on ProductVariant { id title sku price compareAtPrice product { id title handle } } } }
        }
        draftComponents: metafield(namespace: "${NAMESPACE}", key: "${DRAFT_COMPONENTS_KEY}") {
          compareDigest jsonValue
          references(first: 128) { nodes { ... on ProductVariant { id title sku price compareAtPrice product { id title handle } } } }
        }
      }
    }
  `, {id: normalizeProductId(productId)});
  const product = json.data.product;
  if (!product) throw new Error('Product not found.');
  const summary = configurationSummary(product.configuration?.jsonValue);
  const legacySources = [product.configuration?.jsonValue, product.draft?.jsonValue].filter(Boolean);
  if (product.legacyGroup?.value && !product.legacyGroup.reference && (!legacySources.length || legacySources.some((source) => !Object.hasOwn(source, 'productGroupHandle')))) throw new Error('The legacy group is missing. Restore or repair it before migrating settings.');
  const publishedConfiguration = await migrateHandleSettings(product.configuration?.jsonValue, {group: product.legacyGroup?.reference, productSwitcherDraft: null, resolveDisclaimers: fetchDisclaimers});
  const savedDraft = await migrateHandleSettings(product.draft?.jsonValue, {group: product.legacyGroup?.reference, productSwitcherDraft: product.legacySwitcherDraft?.jsonValue, resolveDisclaimers: fetchDisclaimers});
  const componentVariants = (product.draft ? product.draftComponents : product.components)?.references?.nodes || [];
  const configuration = reconcileConfiguration(savedDraft ?? publishedConfiguration, product.options, componentVariants);
  configuration.productGroupHandle ??= product.legacyGroup?.reference?.handle || '';
  configuration.disclaimerHandles ??= [];
  if (!product.draft && product.legacySwitcherDraft?.jsonValue && !Object.hasOwn(product.configuration?.jsonValue || {}, 'productGroupHandle')) {
    const migrated = await migrateHandleSettings(product.configuration?.jsonValue || configuration, {group: product.legacyGroup?.reference, productSwitcherDraft: product.legacySwitcherDraft.jsonValue, resolveDisclaimers: fetchDisclaimers});
    if (migrated?.productSwitcherDraft) configuration.productSwitcherDraft = migrated.productSwitcherDraft;
  }
  return {
    id: product.id,
    title: product.title,
    handle: product.handle,
    status: product.status,
    options: product.options,
    variants: product.variants.nodes,
    configured: Boolean(product.configuration),
    summary,
    configuration,
    savedConfiguration: JSON.stringify(configuration),
    savedDraft,
    publishedConfiguration,
    legacySwitcherDraft: product.legacySwitcherDraft?.jsonValue ?? null,
    digests: {
      [CONFIGURATION_KEY]: product.configuration?.compareDigest ?? null,
      [COMPONENTS_KEY]: product.components?.compareDigest ?? null,
      [DRAFT_CONFIGURATION_KEY]: product.draft?.compareDigest ?? null,
      [DRAFT_COMPONENTS_KEY]: product.draftComponents?.compareDigest ?? null,
      disclaimers: product.notices?.compareDigest ?? null,
      disclaimers_draft: product.draftNotices?.compareDigest ?? null,
    },
    componentVariants,
    adminUrl: `https://admin.shopify.com/store/${json.data.shop.myshopifyDomain.split('.')[0]}/products/${numericId(product.id)}`,
    previewUrl: `https://${json.data.shop.myshopifyDomain}/products/${encodeURIComponent(product.handle)}?view=configurator-preview`,
    publishedUrl: `https://${json.data.shop.myshopifyDomain}/products/${encodeURIComponent(product.handle)}?view=configurator`,
  };
}

export async function fetchVariantDetails(ids: string[]) {
  if (!ids.length) return [];
  const json = await gqlFetch(`#graphql
    query ConfiguratorComponentVariants($ids: [ID!]!) {
      nodes(ids: $ids) {
        ... on ProductVariant { id title sku price compareAtPrice product { id title handle } }
      }
    }
  `, {ids});
  return json.data.nodes.filter(Boolean);
}

async function writeConfiguration(productId: string, configuration: any, digests: Record<string, string | null>, publish = false, additionalFields: any[] = []) {
  if (configuration.noticeIds?.length && configuration.disclaimerHandles === undefined) throw new Error('Reload to migrate disclaimer references before saving.');
  const pairs = [[DRAFT_CONFIGURATION_KEY, DRAFT_COMPONENTS_KEY]];
  if (publish) pairs.push([CONFIGURATION_KEY, COMPONENTS_KEY]);
  const componentIds = collectComponentVariantIds(configuration);
  const metafields = pairs.flatMap(([configKey, componentsKey]) => [
    {ownerId: productId, namespace: NAMESPACE, key: configKey, type: 'json', value: JSON.stringify(authoredConfiguration(configuration)), compareDigest: digests[configKey]},
    {ownerId: productId, namespace: NAMESPACE, key: componentsKey, type: 'list.variant_reference', value: JSON.stringify(componentIds), compareDigest: digests[componentsKey]},
  ]);
  if (additionalFields.length) throw new Error('Group and disclaimer selections must be saved inside settings.');
  if (metafields.some((field) => field.compareDigest === undefined)) throw new Error('Reload this product before saving; revision information is missing.');
  const json = await gqlFetch(`#graphql
    mutation SaveProductConfigurator($metafields: [MetafieldsSetInput!]!) {
      metafieldsSet(metafields: $metafields) {
        metafields { key compareDigest }
        userErrors { field message code }
      }
    }
  `, {metafields: [...metafields, {ownerId: productId, namespace: NAMESPACE, key: CONFIGURED_KEY, type: 'boolean', value: 'true'}]});
  throwUserErrors(json.data.metafieldsSet);
  return {
    configuration,
    savedConfiguration: JSON.stringify(configuration),
    savedDraft: configuration,
    digests: {...digests, ...Object.fromEntries(json.data.metafieldsSet.metafields.map((field: any) => [field.key, field.compareDigest]))},
    ...(publish ? {publishedConfiguration: configuration, configured: true} : {}),
  };
}

export async function saveProductConfiguration(productId: string, configuration: any, digests: Record<string, string | null>, additionalFields: any[] = []) {
  return writeConfiguration(productId, parseDraftConfiguration(configuration), digests, false, additionalFields);
}

export async function publishProductConfiguration(product: any, additionalFields: any[] = []) {
  if (!product.savedDraft || JSON.stringify(product.configuration) !== product.savedConfiguration) throw new Error('Save your draft before publishing.');
  const [health] = await checkConfigurations([product.savedDraft], gqlFetch);
  if (health.issues.length) throw new Error('Cannot publish yet. An option uses a missing item. Choose a replacement or remove it, then save and publish again.');
  // Writing the draft unchanged in this same atomic mutation checks its revision too.
  return writeConfiguration(product.id, parseConfiguration(product.savedDraft), product.digests, true, additionalFields);
}

export async function resetProductDraft(product: any, additionalFields: any[] = []) {
  if (!product.publishedConfiguration) throw new Error('There is no published configuration to restore.');
  return saveProductConfiguration(product.id, product.publishedConfiguration, product.digests, additionalFields);
}

export async function removeProductConfiguration(productId: string) {
  const json = await gqlFetch(`#graphql
    mutation RemoveProductConfigurator($metafields: [MetafieldIdentifierInput!]!) {
      metafieldsDelete(metafields: $metafields) {
        deletedMetafields { ownerId namespace key }
        userErrors { field message }
      }
    }
  `, {metafields: [
    {ownerId: productId, namespace: NAMESPACE, key: CONFIGURATION_KEY},
    {ownerId: productId, namespace: NAMESPACE, key: CONFIGURED_KEY},
    {ownerId: productId, namespace: NAMESPACE, key: COMPONENTS_KEY},
    {ownerId: productId, namespace: NAMESPACE, key: DRAFT_CONFIGURATION_KEY},
    {ownerId: productId, namespace: NAMESPACE, key: DRAFT_COMPONENTS_KEY},
    {ownerId: productId, namespace: NAMESPACE, key: 'disclaimers'},
    {ownerId: productId, namespace: NAMESPACE, key: 'disclaimers_draft'},
  ]});
  throwUserErrors(json.data.metafieldsDelete);
}

export function configurationSummary(value: any) {
  if (!value) return {configured: false, groups: 0, choices: 0, components: 0, error: ''};
  try {
    const configuration = parseConfiguration(convertPrereleaseConfiguration(value));
    const choices = configuration.customOptions.reduce((sum: number, group: any) => sum + group.choices.length, 0);
    return {configured: true, groups: configuration.customOptions.length, choices, components: collectComponentVariantIds(configuration).length, error: ''};
  } catch (error) {
    return {configured: Boolean(value), groups: 0, choices: 0, components: 0, error: error instanceof Error ? error.message : 'Invalid configuration.'};
  }
}

function reconcileConfiguration(value: any, options: any[], componentVariants: any[] = []) {
  let configuration;
  configuration = value ? convertPrereleaseConfiguration(value) : structuredClone(EMPTY_CONFIGURATION);
  const structureErrors = configurationStructureErrors(configuration);
  if (structureErrors.length) throw new Error(structureErrors.join('\n'));
  if (!value) configuration.settings = {...configuration.settings, requireTermsAcceptance: false};
  configuration.shopifyOptions = Array.isArray(configuration.shopifyOptions) ? configuration.shopifyOptions.filter(Boolean) : [];
  const variantById = new Map(componentVariants.map((variant: any) => [variant.id, variant]));
  configuration.customOptions = Array.isArray(configuration.customOptions) ? configuration.customOptions.filter(Boolean).map((group: any) => ({
    ...group,
    label: group.label === 'New add-on group' ? 'New custom option' : group.label,
    status: group.status || 'active',
    conditions: {mode: group.conditions?.mode || 'all', rules: Array.isArray(group.conditions?.rules) ? group.conditions.rules : []},
    choices: Array.isArray(group.choices) ? group.choices.filter(Boolean).map((choice: any) => ({
      ...choice,
      choiceType: choice.choiceType === 'customerInput' || (!choice.choiceType && choice.fields?.length && !choice.components?.length) ? 'customerInput' : 'variants',
      selectedByDefault: Boolean(choice.selectedByDefault),
      components: Array.isArray(choice.components) ? choice.components.map((component: any) => ({
        ...component,
        itemLabel: component.itemLabel || variantItemLabel(variantById.get(component.variantId)),
      })) : [],
      includeItemIds: [],
      fields: Array.isArray(choice.fields) ? choice.fields.filter(Boolean).map((field: any) => ({...field, helpText: field.helpText || ''})) : [],
    })) : [],
  })) : [];
  const legacyNativeItemIds = new Set(configuration.shopifyOptions.flatMap((option: any) => Object.values(option.values || {}).flatMap((mapping: any) => mapping?.includeItemIds || [])));
  configuration.includedItems = Array.isArray(configuration.includedItems) ? configuration.includedItems.filter(Boolean).map((item: any) => {
    const optionValueIds = Array.isArray(item.optionValueIds) ? item.optionValueIds : configuration.shopifyOptions.flatMap((option: any) => Object.entries(option.values || {}).filter(([, mapping]: any) => (mapping?.includeItemIds || []).includes(item.id)).map(([valueId]) => valueId));
    const type = ['base', 'shopifyOption'].includes(item.type) ? item.type : legacyNativeItemIds.has(item.id) ? 'shopifyOption' : 'base';
    return {...item, type, optionValueIds: type === 'shopifyOption' ? optionValueIds : []};
  }) : [];
  const packingKeys = new Set([
    ...configuration.includedItems.map((item: any) => `include:${item.id}`),
    ...configuration.customOptions.flatMap((group: any) => group.choices || []).flatMap((choice: any) => choice.components || []).map((component: any) => `custom:${component.variantId}`),
  ]);
  const packingSeen = new Set<string>();
  configuration.includedItemOrder = (Array.isArray(configuration.includedItemOrder) ? configuration.includedItemOrder : []).filter((key: any) => {
    if (!packingKeys.has(key) || packingSeen.has(key)) return false;
    packingSeen.add(key); return true;
  });
  for (const key of packingKeys) if (!packingSeen.has(key)) configuration.includedItemOrder.push(key);

  configuration.shopifyOptions = options.map((option) => {
    const existing = configuration.shopifyOptions.find((item: any) => item.shopifyOptionId === option.id);
    const valueIds = option.optionValues.map(({id}: any) => numericId(id));
    const values = Object.fromEntries(valueIds.map((id: string) => [id, {...(existing?.values?.[id] || {}), includeItemIds: []}]));
    return existing ? {...existing, name: option.name, position: option.position, optionValueIds: valueIds, values} : {
      id: `native_${numericId(option.id)}`, shopifyOptionId: option.id, name: option.name, position: option.position,
      optionValueIds: valueIds, description: '', values,
    };
  });

  const available = new Set([
    ...configuration.shopifyOptions.map(({id}: any) => `shopifyOption:${id}`),
    ...configuration.customOptions.map(({id}: any) => `customOption:${id}`),
  ]);
  const seen = new Set<string>();
  const order = (Array.isArray(configuration.formOrder) ? configuration.formOrder : []).filter((item: any) => {
    const key = `${item?.type}:${item?.id || ''}`;
    if (!available.has(key) || seen.has(key)) return false;
    seen.add(key);
    return true;
  });
  const ordered = new Set(order.map((item: any) => `${item.type}:${item.id || ''}`));
  for (const item of configuration.shopifyOptions.map(({id}: any) => ({type: 'shopifyOption', id}))) if (!ordered.has(`${item.type}:${item.id}`)) order.push(item);
  for (const item of configuration.customOptions.map(({id}: any) => ({type: 'customOption', id}))) if (!ordered.has(`${item.type}:${item.id}`)) order.push(item);
  configuration.formOrder = order;
  return configuration;
}

function variantItemLabel(variant: any) {
  if (!variant) return '';
  return variant.title && variant.title !== 'Default Title' ? variant.title : variant.product?.title || variant.title || '';
}

function numericId(gid: string) {
  return gid.split('/').pop() || gid;
}

function normalizeProductId(id: string) {
  return id.startsWith('gid://') ? id : `gid://shopify/Product/${id}`;
}
